/** @fileoverview Size steps of one component: moves text, metric and icon sizes along the app's named steps. */

import type { FabrikComponentSize } from '../../types/apps';

/* The named text steps, smallest first; each follows the app's size preset. */
const TYPE_STEPS = [
  'type-caption',
  'type-small',
  'type-body',
  'type-heading',
  'type-title',
  'type-display',
  'type-hero',
] as const;

export type TypeStep = (typeof TYPE_STEPS)[number];

/* How many text steps each component size moves from the usual look. */
const STEP_OFFSETS: Record<FabrikComponentSize, number> = { sm: -1, md: 0, lg: 1, xl: 2 };

/* Icon sizes in pixels for each step; md is the usual 24 px. */
const ICON_PIXELS: Record<FabrikComponentSize, number> = { sm: 20, md: 24, lg: 32, xl: 40 };

/**
 * Gives the text step for a component: its usual step, moved by its size and kept inside the
 * named steps.
 * @param base the usual text step of the component
 * @param size the component's size step, md when left out
 */
export const typeStepFor = (base: TypeStep, size: FabrikComponentSize = 'md'): TypeStep =>
  TYPE_STEPS[
    Math.min(Math.max(TYPE_STEPS.indexOf(base) + STEP_OFFSETS[size], 0), TYPE_STEPS.length - 1)
  ];

/**
 * Gives an icon size in pixels from a size step, or the pixel size older apps set.
 * @param size the icon's size step or pixel size, md when left out
 */
export const iconPixelsFor = (size: FabrikComponentSize | number = 'md'): number =>
  typeof size === 'number' ? size : ICON_PIXELS[size];
